import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { List, ListItem, ListItemIcon, ListItemText, makeStyles, Theme, createStyles, IconButton } from '@material-ui/core';
import { PasslyInfiniteScroll, PasslyButton } from '@app/components';
import { useTypedTranslation } from '@app/hooks';
import { vaultFoldersService } from '@app/services';
import { infiniteScrollItemsFetchLimit, searchMinimumLength, passwordFilter } from '@app/helpers';
import { FormMode } from '@app/helpers/enums';
import { IVaultFolder, Order } from '@app/models';
import { FolderOutlined, Create, DeleteOutline, Add } from '@material-ui/icons';
import { vaultUpdate, vaultDelete, vaultAdd } from '@app/store/constants';
import { confirm } from '@app/components/Modal/confirmModal';
import { toast } from 'react-toastify';
import { AddEditFolderDialog } from '@app/pages/password/dialogs/addEditFolder.dialog';
import { ModalComponent, IModalData } from '@app/components';
import { PasslyTooltip } from '@app/componentsV2';

interface IProps {
    vaultId: string;
    folderClickHandler: Function;
    queryFolderId: string;
    queryFolderName: string;
    filterString: string;
    searchType: string;
    foldersLoadingCallback: Function;
    permissions: Set<string>;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            '&.Mui-selected': {
                backgroundColor: theme.palette.secondary.main
            }
        }
    })
);

const FolderListComponent: React.SFC<IProps> = ({
    vaultId,
    folderClickHandler,
    queryFolderId,
    queryFolderName,
    filterString,
    searchType,
    foldersLoadingCallback,
    permissions
}) => {
    const { translation } = useTypedTranslation();

    const [rows, setRows] = useState<IVaultFolder[]>([]);
    const [foldersCount, setFoldersCount] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [initialLoad, setInitialLoad] = useState(true);
    const [hovered, setHovered] = useState(-1);
    const [openAddFolderModal, setOpenAddFolderModal] = useState(false);
    const [openEditFolderModal, setOpenEditFolderModal] = useState(false);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    // local infinte scroll resetter
    const [reset, setReset] = useState(0);

    // Keeps track of the internally selected folder id so
    // the correct row is always highlighted
    const [selectedFolderId, setSelectedFolderId] = useState('');

    const classes = useStyles();

    useEffect(() => {
        if ((!initialLoad && filterString === '') || filterString.length >= searchMinimumLength) {
            resetData();
        }
    }, [filterString]);

    useEffect(() => {
        if (!initialLoad && filterString.length >= searchMinimumLength) {
            resetData();
        }
    }, [searchType]);

    useEffect(() => {
        if (rows.length > 0) {
            let foundIndex = 0;

            // the rows has the previously selected folder id, keep it set
            if (
                rows.filter((folder, index) => {
                    if (folder.Id === selectedFolderId) {
                        foundIndex = index;
                        return true;
                    }
                }).length > 0
            ) {
                folderClickHandler(selectedFolderId);
                setSelectedFolderId(selectedFolderId);
                setSelectedIndex(foundIndex);
            } else if (selectedFolderId === '' && queryFolderId !== '') {
                folderClickHandler(queryFolderId);
                setSelectedFolderId(queryFolderId);
                setSelectedIndex(0);
            }
            // no matches, set to first item
            else if (selectedFolderId === '') {
                folderClickHandler(rows[0].Id);
                setSelectedFolderId(rows[0].Id);
                setSelectedIndex(0);
            }
        } else {
            folderClickHandler('');
        }
    }, [rows]);

    const resetData = () => {
        setReset(reset + 1);
        foldersLoadingCallback(true);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        const isPasswordFilter = searchType === passwordFilter;
        if (isReset) {
            foldersLoadingCallback(true);
        }

        vaultFoldersService
            .getVaultFolders(vaultId, 'DisplayName', Order.asc, infiniteScrollItemsFetchLimit, skipCount, isReset, filterString, isPasswordFilter)
            .then((response: any) => {
                let folders: IVaultFolder[] = response.data.value;
                let filteredFolders = folders;

                if (isReset) {
                    setFoldersCount(response.data['@odata.count']);
                }

                if (queryFolderId !== '') {
                    const item = folders.find((folder) => folder.Id === queryFolderId);

                    if (item) {
                        // logic for when query parameters contains a specific folder
                        filteredFolders = folders.filter((folder) => {
                            return folder.Id !== queryFolderId;
                        });
                        filteredFolders.unshift(item);
                    }
                }

                let newRows = filteredFolders.map((folder: any) => {
                    const data: any = {
                        Id: folder.Id,
                        IsDefault: folder.IsDefault,
                        DisplayName: folder.DisplayName
                    };
                    return data;
                });

                if (newRows.length === 0) {
                    setRows([]);
                } else if (isReset) {
                    setRows(newRows);
                } else {
                    setRows(rows.concat(newRows));
                }

                if (initialLoad) {
                    setInitialLoad(false);
                }

                foldersLoadingCallback(false);
            });
    };

    const handleListItemClick = (id: string, index: number) => {
        folderClickHandler(id);
        setSelectedFolderId(id);
        setSelectedIndex(index);
    };

    const handlePageRefreshFromDialog = (refresh: boolean) => {
        if (refresh) {
            resetData();
        }
    };

    const deleteRow = (id: string) => () => {
        confirm(
            translation('DELETE_VAULT_FOLDER_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_VAULT_FOLDER_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                vaultFoldersService.deleteVaultFolders([id], vaultId, handleDeleteSuccess, handleDeleteError);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleDeleteSuccess = () => {
        resetData();
    };

    const handleDeleteError = () => {
        toast(translation('DELETE_VAULT_FOLDER_ERROR'));
    };

    const handleModalCloseCallback = (close?: boolean) => {
        if (close !== undefined) {
            setOpenAddFolderModal(false);
        } else {
            setOpenAddFolderModal(!openAddFolderModal);
        }
    };

    const handleModalCloseEditCallback = (close?: boolean) => {
        if (close !== undefined) {
            setOpenEditFolderModal(false);
        } else {
            setOpenEditFolderModal(!openEditFolderModal);
        }
    };

    const editFolderClick = (folderId: string, name: string) => {
        setOpenEditFolderModal(true);
        setModalData({
            headerTitle: translation('FOLDER_EDIT'),
            form: (
                <AddEditFolderDialog
                    formMode={FormMode.Edit}
                    folderId={folderId}
                    folderName={name}
                    vaultId={vaultId}
                    handleRefresh={handlePageRefreshFromDialog}
                    dialogOpen={handleModalCloseEditCallback}
                />
            )
        });
    };

    const addFolder = () => {
        setOpenAddFolderModal(true);
        setModalData({
            headerTitle: translation('NEW_FOLDER'),
            form: (
                <AddEditFolderDialog
                    formMode={FormMode.Add}
                    vaultId={vaultId}
                    handleRefresh={handlePageRefreshFromDialog}
                    dialogOpen={handleModalCloseCallback}
                />
            )
        });
    };

    const getEndActions = (id: string, index: number, folderName: string) => {
        return (
            <>
                {permissions.has(vaultUpdate) && (
                    <IconButton edge='end' size='small' onClick={() => editFolderClick(id, folderName)}>
                        <Create color='primary' />
                    </IconButton>
                )}
                {permissions.has(vaultDelete) && (selectedIndex === index || hovered === index) && (
                    <IconButton edge='end' size='small' onClick={deleteRow(id)}>
                        <DeleteOutline color='primary' />
                    </IconButton>
                )}
            </>
        );
    };

    return (
        <>
            <ModalComponent localModalSwitcher={openAddFolderModal} modalSwitcherCallback={handleModalCloseCallback} {...modalData} />
            <ModalComponent localModalSwitcher={openEditFolderModal} modalSwitcherCallback={handleModalCloseEditCallback} {...modalData} />

            <div className='vaults__table-header'>
                <div className={`vaults__table-header--folders`} />
                <PasslyInfiniteScroll totalCount={foldersCount} currentCount={rows.length} onFetch={fetchMoreData} resetLocal={reset}>
                    <List>
                        {rows.map((folder: IVaultFolder, index: number) => (
                            <PasslyTooltip title={folder.DisplayName} key={folder.DisplayName + index}>
                                <ListItem
                                    className={`tile__folder-vault ${classes.root}`}
                                    button
                                    key={index}
                                    selected={selectedIndex === index}
                                    onMouseEnter={() => setHovered(index)}
                                    onMouseLeave={() => setHovered(-1)}
                                    onClick={() => handleListItemClick(folder.Id, index)}
                                >
                                    <ListItemIcon>
                                        <FolderOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary={folder.DisplayName} />
                                    <div className='vault__passwords--endactions'>
                                        {(selectedIndex === index || hovered === index) &&
                                            !folder.IsDefault &&
                                            getEndActions(folder.Id, index, folder.DisplayName)}
                                    </div>
                                </ListItem>
                            </PasslyTooltip>
                        ))}
                    </List>
                </PasslyInfiniteScroll>
                {permissions.has(vaultAdd) && (
                    <PasslyButton variant='text' color='primary' startIcon={<Add color='inherit' />} onClick={addFolder}>
                        {translation('NEW_FOLDER')}
                    </PasslyButton>
                )}
            </div>
        </>
    );
};

export default FolderListComponent;
